import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { useUser } from "@/web/auth/useUser";
import { Redirect } from "@/web/components/Redirect";
import { Button } from "@/web/components/primitives/button";
import { ErrorHandler } from "@/web/lib/errors";
import { tableQueryOptions, useProcedureMutation } from "@/web/lib/tanstack";
import { procedures, tables } from "@tycho/web-bindings";
import {
  aboutYouDataFromUserInfo,
  AboutYouForm,
  buildPartialAboutYouUserInfoMutationArgs,
  useAboutYouForm,
} from "@/web/routes/_protected/smb/-onboarding/AboutYouForm";

export const Route = createFileRoute("/_protected/onboarding/inv")({
  component: InvOnboarding,
});

function InvOnboarding() {
  const user = useUser();
  if (!user) {
    return null;
  }
  // The onboarding fork mints the investor profile before routing here, so a
  // caller who somehow lands without one is sent back to pick a path.
  if (user.investorIds.length === 0) {
    return <Redirect to="/onboarding" replace />;
  }
  return <InvOnboardingContent />;
}

/**
 * Investor "About You" step. Unlike SMB onboarding this is entirely optional and
 * non-blocking: the investor already has a profile (minted at the fork) and can
 * browse, pledge, and reach their dashboard without ever completing this form.
 * Saving is a plain partial `upsertUserInfo` — no brand mint, no gating. The
 * personal info collected here is what later powers "Create my investor account"
 * on the dashboard.
 */
function InvOnboardingContent() {
  const user = useUser();
  const navigate = useNavigate();

  const { data: userInfoRows } = useSuspenseQuery(tableQueryOptions(tables.currentUserInfo));
  const existingUserInfo = userInfoRows[0];
  const prefill = existingUserInfo
    ? aboutYouDataFromUserInfo(existingUserInfo, user?.email ?? "")
    : undefined;
  const { aboutYouForm, aboutYouData } = useAboutYouForm({
    defaultEmail: user?.email,
    prefill,
  });

  const { mutateAsync: upsertUserInfo, isPending: isSaving } = useProcedureMutation(
    procedures.upsertUserInfo,
  );

  async function handleSaveAndContinue() {
    const isValid = await aboutYouForm.trigger();
    if (!isValid) return;
    try {
      // Preserve any stored avatar (empty/undefined clears it — see
      // `upsertUserInfo`'s empty-string-clears semantics).
      await upsertUserInfo({
        ...buildPartialAboutYouUserInfoMutationArgs(aboutYouData, true),
        avatarCdnPath: existingUserInfo?.avatarCdnPath,
      });
      await navigate({ to: "/profile/inv" });
    } catch (error) {
      ErrorHandler.default(error);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-2xl flex-col gap-6 p-6">
      <header className="flex flex-col gap-2">
        <h1 className="font-heading text-2xl uppercase">Welcome, investor</h1>
        <p className="text-muted-foreground">
          Tell us a bit about yourself. This is optional — you can browse and back businesses right
          away, and finish this whenever you&apos;re ready. We&apos;ll need it before you create
          your investor account.
        </p>
      </header>

      <AboutYouForm form={aboutYouForm} showAccreditation />

      <div className="flex items-center justify-between gap-3">
        <Button
          variant="ghost"
          onClick={() => {
            void navigate({ to: "/profile/inv" });
          }}
        >
          Skip for now
        </Button>
        <Button disabled={isSaving} onClick={() => void handleSaveAndContinue()}>
          {isSaving ? "Saving…" : "Save & continue"}
        </Button>
      </div>
    </div>
  );
}
